import { SvgIcon, Paper, Box, Popper, Stack, IconButton, Dialog, DialogContent } from '@mui/material';
import React, { useContext, useState } from 'react';
import { palette, spaces, borderRadius, customSizes, boxShadows,
  mobileViewportFullHeight } from '../../../assets/styles/themes/tokens';
import getMuiIcon from '../../../utils/getMuiIcon';
import ReaderDetails from '../ReaderDetails/ReaderDetails';
import SearchInput, { focusOnCustomSearch } from './SearchInput';
import { ReaderContext } from '../../Reader/ReaderContext';
import { FloatingToolbarCommonProps } from './FloatingToolbarBase';
import { AddMenu, PinAction } from '../../cards/MuiCard/MuiCardBase/MuiCardBase';
import { useSelector } from 'react-redux';
import { selectMindItem } from '../../../reducers/mindItems.selectors';
import { OnboardingHint } from '../../OnboardingHint';
import { useTranslation } from 'react-i18next';
import { noop } from 'lodash';
import { Hint } from '../../Hint';
import { onboardingHints } from '../../../constants';
import useScreenSize from '../../../utils/useScreenSize';
import { getPinnedAlerts } from '../NewReader';

const menuBottomMargin = parseInt(spaces.xxs) + parseInt(customSizes.floatingToolbar.padding);

const FloatingBarSmallContent : React.FC<FloatingToolbarCommonProps> = (props) => {
  type VisibleMenu = 'readerDetails' | 'addToCollection' | 'search';

  const [visibleMenu, setVisibleMenu] = useState<VisibleMenu>();
  const { mindItemId, onboardingStepCounterMobile, setAlert, isInCollection, isPinned } = useContext(ReaderContext);
  const { t } = useTranslation();
  const { searchInputProps } = props;
  const { isMediumScreen } = useScreenSize();
  const anchorEl = React.useRef<HTMLElement>(null);
  const mindItem = useSelector(state => selectMindItem(state, mindItemId));

  const pinnedAlerts = getPinnedAlerts(t);

  const onPinAction = (action: PinAction) => {
    setAlert?.(action === 'pin' ? pinnedAlerts.addedToFavAlert : pinnedAlerts.removedFromFavAlert);
  };

  if (!mindItem) {
    return null;
  }

  const iconButtonSx = {
    '& svg': {
      height: 20,
      width: 20,
    },
    alignSelf: 'center',
    color: palette.text.secondary,
    '&:hover': {
      background: palette.primary.selected,
    },
  };

  const searchInput = <Paper
    elevation={4}
    sx={{
      padding: spaces.xs,
      borderRadius: borderRadius.sm,
    }}
  ><SearchInput
      onChange={searchInputProps?.onSearchInputChange}
      onNext={searchInputProps?.onSearchNext}
      onPrevious={searchInputProps?.onSearchPrevious}
      endAdornment={searchInputProps?.endAdornment}
      placeholder={searchInputProps?.placeholder}
      isDisabled={searchInputProps?.isDisabled}
      inputProps={{
        sx: {
          width: isMediumScreen ? customSizes.searchInput.width.tablet : customSizes.searchInput.width.mobile,
        }
      }}
      value={searchInputProps?.value}
    />
  </Paper>;

  const getDialogContent = (menu?: VisibleMenu) => {
    switch (menu) {
      case 'readerDetails':
        return <ReaderDetails
          mindItemId={mindItemId}
          topRightAction={{
            icon: <SvgIcon component={getMuiIcon('close')} />,
            onClick: () => setVisibleMenu(undefined),
            buttonSx: {
              marginRight: `-${spaces.sm}`,
            }
          }}
        />;
      case 'addToCollection':
        return <AddMenu
          isOpen={visibleMenu === 'addToCollection'}
          onClose={() => setVisibleMenu(undefined)}
          pinActionCallback={onPinAction}
          type='document'
          id={mindItemId}
          isPinned={!!mindItem?.isPinned}
          itemData={mindItem}
        />;
      default:
        return;
    }
  };

  const toggleMenu = (menu: VisibleMenu) => {
    if (visibleMenu === menu) {
      setVisibleMenu(undefined);
    } else {
      setVisibleMenu(menu);
    }
  };

  const anchorElTop = anchorEl.current?.getBoundingClientRect().top;
  const readerDetailsBottomMargin = spaces.xxxs;
  const readerDetailsTopMargin = '56px';
  const readerDetailsMaxHeight =
    (anchorEl.current ? `${anchorEl.current.getBoundingClientRect().top  - (
      parseInt(readerDetailsTopMargin) +
      parseInt(readerDetailsBottomMargin)
    )}px` : 'auto');
  
  const bookmarkIcon = (isInCollection || isPinned) ? 
    <SvgIcon viewBox='0 0 16 19' component={getMuiIcon('bookmarkAdded')} />:
    <SvgIcon viewBox='0 0 22 22' component={getMuiIcon('bookmark')} />;

  return <>
    {
      <Popper
        anchorEl={anchorEl.current}
        open={visibleMenu === 'search'}
        placement='top'
      >
        <Box sx={{
          marginBottom: `${menuBottomMargin}px`,
        }}>
          {searchInput}
        </Box>
      </Popper>
    }
    <Dialog
      open={visibleMenu === 'readerDetails' || visibleMenu === 'addToCollection'}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-end',
        }
      }}
      PaperProps={
        {
          sx: {
            boxShadow: boxShadows.elevation2,
            maxHeight: readerDetailsMaxHeight,
            borderRadius: borderRadius.sm,
            display: 'flex',
            marginTop: readerDetailsTopMargin,
            // this calculation is to make sure the dialog appears above the floating toolbar
            // anchorElTop is where the floating tooolbar starts from the top of the screen, excluding
            // the floating toolbar padding and the reader details bottom margin. 
            // that is why we add the floating toolbar padding and the reader details bottom margin to it
            // We then subtract it from the mobile viewport full height to get the marginBottom
            marginBottom: `calc(
              ${mobileViewportFullHeight} -
              ${(anchorElTop ?? 0) - 
              parseInt(readerDetailsBottomMargin) -
              parseInt(customSizes.floatingToolbar.padding)}px)`,
            marginX: 0,
          }
        }
      }
    >
      <DialogContent
        sx={{
          padding: 0,
          '& .MuiPaper-root': {
            marginTop: 0
          },
        }}
      >
        {getDialogContent(visibleMenu)}
      </DialogContent>
    </Dialog>
    <OnboardingHint
      id={onboardingHints.newReaderMobile}
      placement='top'
      isNew={true}
      title={t('Welcome to the new document reader!')}
      description={t('We have optimized the interface for you: ' +
        'Information about the unit and the entire work can now be found here. ' +
        'Would you like to learn more? ' +
        'Start the tour or check out our FAQ.')}
      actions={{
        primary: {
          label: t('Make a tour'),
          onClick: () => onboardingStepCounterMobile?.startOnboarding(),
        },
        secondary: {
          label: t('Explore for yourself'),
          onClick: noop,
        },
      }}
    >
      <Box>
        <Box ref={anchorEl}>
          <Stack
            spacing={1}
            direction='row'
          >
            <IconButton
              sx={iconButtonSx}
              aria-expanded={visibleMenu === 'addToCollection'}
              onClick={() => {
                setVisibleMenu('addToCollection');
              }}
            >
              { bookmarkIcon }
            </IconButton>
            <IconButton
              sx={iconButtonSx}
              aria-expanded={visibleMenu === 'readerDetails'}
              onClick={() => {
                toggleMenu('readerDetails');
              }} 
            >
              <SvgIcon viewBox='0 0 15 15' component={getMuiIcon('book')} />
            </IconButton>
            <Hint
              open={onboardingStepCounterMobile?.step === 3}
              placement='top'
              bottomLeftText='3/3'
              description={t('Search the documents for keywords.')}
              onClose={() => onboardingStepCounterMobile?.dismiss()}
              actions={{
                primary: {
                  label: t('Close'),
                  onClick: () => onboardingStepCounterMobile?.dismiss(),
                  buttonProps: {
                    variant: 'text',
                    size: 'small',
                    sx: {
                      color: palette.primary.contrastText,
                    }
                  }
                }
              }}
              useDesktopButtonDesign={true}
              sx={{
                width: 250,
              }}
            >
              <Box
                onClick={() => {
                  if (searchInputProps?.isDisabled) {
                    setAlert?.({
                      message: t('Search is not possible in this document.'),
                      severity: 'info',
                    });
                  }
                }}
              >
                <IconButton
                  sx={iconButtonSx}
                  aria-expanded={visibleMenu === 'search'}
                  onClick={(event) => {
                    if (!searchInputProps?.isDisabled) {
                      toggleMenu('search');
                      setTimeout(() => focusOnCustomSearch(), 1);
                      event.stopPropagation();
                      event.preventDefault();
                    } else {
                      setAlert?.({
                        message: t('Search is not possible in this document.'),
                        severity: 'info',
                      });
                    }
                    if (onboardingStepCounterMobile?.step === 3) onboardingStepCounterMobile?.dismiss();
                  }}
                  disabled={searchInputProps?.isDisabled}
                >
                  <SvgIcon viewBox='0 0 21 21' component={getMuiIcon('searchStatus')} />
                </IconButton>
              </Box>
            </Hint>
          </Stack>
        </Box>
      </Box>
    </OnboardingHint>
  </>;
};

export default FloatingBarSmallContent;
